import { CarrierService } from '@/services/main/carrier/carrierService';
import { globalApiService } from '../globalApiSlice';
import type {
  AddCarrierRequestType,
  AddCarrierResponseInterface,
  AddModelResponse,
  AddQuickVehicleResponseInterface,
  AddSellerResponseInterface,
  AddUpdateDealerResponseI,
  AddUpdateSellerResponseI,
  BookingInfoResponse,
  CarrierByIdForViewInterface,
  CarrierDocument,
  CarrierForEditResponse,
  CarrierHistory,
  CarrierMaster,
  CarriersFromSavedQueue,
  CategoriesInterface,
  ConfirmDeliveryOfDocumentsRequestI,
  CountryInterface,
  CustomerInterface,
  DataInterface,
  DealerSellerAuctionConvertedInterface,
  DealerSellerAuctionInterface,
  DeliverableCarrier,
  ExportCategoriesInterface,
  ImageByCarrierId,
  LocationInterface,
  MakeItem,
  MakeResponse,
  MasterDealerResponseI,
  MasterSellerResponseI,
  ModelOptionsInterface,
  ModelResponse,
  ModifyImageRequestInterface,
  ModifyImageSequenceInterface,
  NullableAddUpdateDealerRequestT,
  NullableAddUpdateSellerRequestT,
  PaginatedListCarriers,
  PatchQuotationRequestInterface,
  PatchVitalsRequestType,
  ShareByEmailMultiRequestInterface,
  ShareByEmailRequestInterface,
  UploadDealerStockRequestInterface,
  UploadDealerStockResponseInterface,
} from '@/types/carrier-types';
import type { StoreBaseResponse } from '@/types/types';
import { createCustomError, createCustomResponse } from '@/helpers/utils';
import type { CostPurchaseRequestType } from '@/modules/main/carrier/edit/components/components/tabs/cost-purchase/costPurchaseSchema';
import type { AddQuoteRequestType } from '@/modules/main/carrier/view/components/tabs/components/bookingInfo/components/add-quote/addQuoteSchema';
import type { AddBookingRequestType } from '@/modules/main/carrier/view/components/components/add-booking/addBookingSchema';
import type { AddSellerRequestType } from '@/modules/main/carrier/add/components/components/components/AddSeller/AddSellerSchema';
import type { CarrierFiltersSchemaType } from '@/modules/main/carrier/list/components/filter/filtersSchema';
import type { MoreInfoSchemaOutput } from '@/modules/main/carrier/edit/components/components/tabs/more-info/moreInfoSchema';
import { carrierActions } from '@/store/slices/main/carrier/carrier-slice';
import type { RecursiveNullable } from '@/types/component';
import type { RequestOptions } from '@/services/apiService';
import type AuctionHouseResponse from '@/types/carrier-types';
import type { AddQuickVehicleRequestType } from '@/modules/export/auction/list/components/AddQuickRiksoPopup/addQuickVehicleSchema';

const service = new CarrierService();

const carriersApi = globalApiService.injectEndpoints({
  endpoints: (build) => ({
    addCarrier: build.mutation<
      StoreBaseResponse<AddCarrierResponseInterface>,
      RecursiveNullable<AddCarrierRequestType>
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addCarrier(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_modifyAdd'],
    }),
    getCarrierByIdForView: build.query<
      StoreBaseResponse<CarrierByIdForViewInterface>,
      string
    >({
      queryFn: async (carrierId, { dispatch }) => {
        try {
          const response = await service.getCarrierByIdForView(carrierId);
          dispatch(carrierActions.setViewCarrierById(response));
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_modifyAdd'],
    }),
    getCarrierByIdForEdit: build.query<
      StoreBaseResponse<CarrierForEditResponse>,
      string
    >({
      queryFn: async (carrierId, { dispatch }) => {
        try {
          const response = await service.getCarrierByIdForEdit(carrierId);
          dispatch(
            carrierActions.setEditCarrierById({
              data: response,
              isFetching: null,
              isLoading: null,
            }),
          );
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_modifyAdd'],
    }),
    getBookingInfo: build.query<StoreBaseResponse<BookingInfoResponse>, string>(
      {
        queryFn: async (carrierId) => {
          try {
            const response = await service.getBookingInfo(carrierId);
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
        providesTags: ['carriers_bookingModifyAdd'],
      },
    ),
    getDealers: build.query<
      StoreBaseResponse<DealerSellerAuctionConvertedInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getDealers();
          const converted: DealerSellerAuctionConvertedInterface[] =
            response?.data?.map((item) => {
              return {
                ...item,
                type: 'dealer',
              };
            }) || [];

          return createCustomResponse({ ...response, data: converted });
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getSellers: build.query<
      StoreBaseResponse<DealerSellerAuctionConvertedInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getSellers();
          const converted: DealerSellerAuctionConvertedInterface[] =
            response?.data?.map((item) => {
              return {
                ...item,
                type: 'seller',
              };
            }) || [];

          return createCustomResponse({ ...response, data: converted });
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_addSeller'],
    }),

    getCurrency: build.query<StoreBaseResponse<string[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getCurrencies();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCountries: build.query<
      StoreBaseResponse<CountryInterface[]>,
      string | undefined
    >({
      queryFn: async () => {
        try {
          const response = await service.getCountries();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      keepUnusedDataFor: 24 * 60 * 60 * 1000,
    }),
    addSeller: build.mutation<
      StoreBaseResponse<AddSellerResponseInterface>,
      AddSellerRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addSeller(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_addSeller'],
    }),
    patchCostPurchase: build.mutation<
      StoreBaseResponse<null>,
      CostPurchaseRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await service.patchCosts(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_modifyAdd'],
    }),
    patchVitals: build.mutation<
      StoreBaseResponse<null>,
      PatchVitalsRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await service.patchVitals(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_modifyAdd'],
    }),
    patchMoreInfo: build.mutation<
      StoreBaseResponse<null>,
      MoreInfoSchemaOutput
    >({
      queryFn: async (data) => {
        try {
          const response = await service.patchMoreInfo(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: (_, error) => (error ? [] : ['carriers_modifyAdd']),
    }),
    patchCosts: build.mutation<
      StoreBaseResponse<null>,
      CostPurchaseRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await service.patchCosts(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: (_, error) => (error ? [] : ['carriers_modifyAdd']),
    }),
    patchQuote: build.mutation<
      StoreBaseResponse<unknown>,
      PatchQuotationRequestInterface
    >({
      queryFn: async (data) => {
        try {
          const response = await service.patchQuote(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: (_, error) =>
        error ? [] : ['carriers_bookingModifyAdd'],
    }),
    addQuote: build.mutation<StoreBaseResponse<null>, AddQuoteRequestType>({
      queryFn: async (data) => {
        try {
          const response = await service.addQuote(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_bookingModifyAdd'],
    }),
    putBooking: build.mutation<StoreBaseResponse<null>, AddBookingRequestType>({
      queryFn: async (data) => {
        try {
          const response = await service.putBooking(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_modifyAdd', 'carriers_bookingModifyAdd'],
    }),
    addQuickVehicle: build.mutation<
      StoreBaseResponse<AddQuickVehicleResponseInterface>,
      AddQuickVehicleRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addQuickVehicle(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_modifyAdd', 'transport_ModifyAdd'],
    }),
    getCarriersList: build.query<
      StoreBaseResponse<PaginatedListCarriers>,
      CarrierFiltersSchemaType['queryParameters']
    >({
      queryFn: async (params) => {
        try {
          const response = await service.getCarriers(params);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_modifyAdd'],
    }),
    getCarrierMakes: build.query<StoreBaseResponse<DataInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getCarrierMakes();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarrierModels: build.query<
      StoreBaseResponse<ModelOptionsInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getCarrierModels();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCustomers: build.query<StoreBaseResponse<CustomerInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getCustomers();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarrierStatus: build.query<StoreBaseResponse<DataInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getCarrierStatus();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarrierTypes: build.query<StoreBaseResponse<DataInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getCarrierTypes();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarriersListExport: build.mutation<
      StoreBaseResponse<unknown>,
      CarrierFiltersSchemaType['queryParameters']
    >({
      queryFn: async (params) => {
        try {
          const response = await service.getCarriersForExport(params);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getImagesByCarrierId: build.query<
      StoreBaseResponse<ImageByCarrierId[]>,
      string
    >({
      queryFn: async (carrierId) => {
        try {
          const response = await service.getImagesByCarrierId(carrierId);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_imageUpdate'],
    }),
    getFuelTypes: build.query<StoreBaseResponse<DataInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getFuelTypes();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getStatusTypes: build.query<
      StoreBaseResponse<
        {
          name: string;
          id: number;
        }[]
      >,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getStatusTypes();
          const a = Object.keys(response?.data || []).map((name) => {
            const id = response?.data?.[name];
            return { name: name, id: Number(id) };
          });
          return createCustomResponse({ ...response, data: a });
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarrierCategories: build.query<
      StoreBaseResponse<CategoriesInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getCarrierCategories();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarrierExportCategories: build.query<
      StoreBaseResponse<ExportCategoriesInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getCarrierExportCategories();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getTransmissionTypes: build.query<StoreBaseResponse<DataInterface[]>, void>(
      {
        queryFn: async () => {
          try {
            const response = await service.getTransmissionTypes();
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
      },
    ),
    getLocations: build.query<StoreBaseResponse<LocationInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getLocations();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_addSeller'],
    }),
    getAuctionHouses: build.query<
      StoreBaseResponse<DealerSellerAuctionInterface[]>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getAuctionHouses();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    uploadImages: build.mutation<
      StoreBaseResponse<null>,
      { formData: FormData; uploadProgress: (percent: number) => void }
    >({
      queryFn: async ({ formData, uploadProgress }) => {
        try {
          const response = await service.uploadImages(formData, uploadProgress);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_imageUpdate', 'carriers_modifyAdd'],
    }),
    modifyImageSettings: build.mutation<
      StoreBaseResponse<null>,
      ModifyImageRequestInterface
    >({
      queryFn: async (data) => {
        try {
          const response = await service.modifyImageSettings(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_imageUpdate', 'carriers_modifyAdd'],
    }),
    modifyImageSequence: build.mutation<
      StoreBaseResponse<null>,
      ModifyImageSequenceInterface
    >({
      queryFn: async (data) => {
        try {
          const response = await service.modifyImageSequence(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_imageUpdate', 'carriers_modifyAdd'],
    }),
    getDocumentsByCarrierId: build.query<
      StoreBaseResponse<CarrierDocument[]>,
      string
    >({
      queryFn: async (carrierId) => {
        try {
          const response = await service.getCarrierDocuments(carrierId);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['carriers_documentDeleteUpdate'],
    }),
    downloadCarrierDocument: build.mutation<StoreBaseResponse<unknown>, number>(
      {
        queryFn: async (docId) => {
          try {
            const response = await service.downloadCarrierDocument(docId);
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
      },
    ),
    previewCarrierDocument: build.mutation<StoreBaseResponse<unknown>, number>({
      queryFn: async (docId) => {
        try {
          const response = await service.downloadCarrierDocument_preview(docId);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    uploadCarrierDocuments: build.mutation<StoreBaseResponse<string>, FormData>(
      {
        queryFn: async (data) => {
          try {
            const response = await service.addCarrierDocument(data);
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
        invalidatesTags: ['carriers_documentDeleteUpdate'],
      },
    ),
    deleteCarrierDocuments: build.mutation<
      StoreBaseResponse<null>,
      {
        carrierId: number;
        docId: number;
      }
    >({
      queryFn: async ({ docId, carrierId }) => {
        try {
          const response = await service.deleteCarrierDocument(
            carrierId,
            docId,
          );
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_documentDeleteUpdate'],
    }),
    getCarrierHistory: build.query<StoreBaseResponse<CarrierHistory[]>, string>(
      {
        queryFn: async (carrierId) => {
          try {
            const response = await service.getCarrierHistory(carrierId);
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
        // new data is always fetched on mount, if changes occur due to in page modifications, use invalidatesTags
        providesTags: ['carriers_bookingModifyAdd'],
      },
    ),
    getAllCarriers: build.query<StoreBaseResponse<CarrierMaster[]>, string>({
      queryFn: async () => {
        try {
          const response = await service.getAllCarriers();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getDeliverableCarriers: build.query<
      StoreBaseResponse<DeliverableCarrier[]>,
      RequestOptions['params']
    >({
      queryFn: async (data) => {
        try {
          const response = await service.getDeliverableCarriers(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    markStatusAsReadyToDeliver: build.mutation<StoreBaseResponse<null>, number>(
      {
        queryFn: async (carrierId) => {
          try {
            const response = await service.markStatusAsDelivered(carrierId);
            return createCustomResponse(response);
          } catch (error) {
            return createCustomError(error);
          }
        },
        invalidatesTags: ['carriers_bookingModifyAdd', 'carriers_modifyAdd'],
      },
    ),
    confirmDeliveryOfDocuments: build.mutation<
      StoreBaseResponse<null>,
      ConfirmDeliveryOfDocumentsRequestI
    >({
      queryFn: async (data) => {
        try {
          const response = await service.confirmDeliveryOfDocuments(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['carriers_bookingModifyAdd', 'carriers_modifyAdd'],
    }),
    getAllMake: build.query<StoreBaseResponse<MakeResponse>, string>({
      queryFn: async () => {
        try {
          const response = await service.getMasterCarrierMake();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['get_master_make'],
    }),
    addMasterMake: build.mutation<StoreBaseResponse<MakeResponse>, MakeItem>({
      queryFn: async (data) => {
        try {
          const response = await service.addMasterCarrierMake(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['get_master_make'],
    }),
    addMasterMakeChange: build.mutation<
      StoreBaseResponse<MakeResponse>,
      MakeItem
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addMasterCarrierMake(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getModelsByMake: build.mutation<
      StoreBaseResponse<ModelResponse>,
      ModelResponse
    >({
      queryFn: async (data) => {
        try {
          const response = await service.getMasterCarrierModel(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    addModelsByMake: build.mutation<
      StoreBaseResponse<AddModelResponse>,
      AddModelResponse
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addMasterCarrierModel(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getMasterAuctionHouse: build.query<
      StoreBaseResponse<AuctionHouseResponse>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getMasterAuctinHouse();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['get_master_auction_house'],
    }),
    shareByEmail: build.mutation<
      StoreBaseResponse<null>,
      ShareByEmailRequestInterface
    >({
      queryFn: async (data) => {
        try {
          const response = await service.shareByEmail(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    shareByEmailMultiCarriers: build.mutation<
      StoreBaseResponse<null>,
      ShareByEmailMultiRequestInterface
    >({
      queryFn: async (data) => {
        try {
          const response = await service.shareByEmailMultiCarriers(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getMasterSellers: build.query<
      StoreBaseResponse<MasterSellerResponseI>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getMasterSellers();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['master_sellers'],
    }),
    addUpdateMastersSellers: build.mutation<
      StoreBaseResponse<AddUpdateSellerResponseI>,
      NullableAddUpdateSellerRequestT
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addUpdateSeller(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['master_sellers'],
    }),
    getMasterDealers: build.query<
      StoreBaseResponse<MasterDealerResponseI>,
      void
    >({
      queryFn: async () => {
        try {
          const response = await service.getMasterDealers();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['master_dealers'],
    }),
    addUpdateMastersDealers: build.mutation<
      StoreBaseResponse<AddUpdateDealerResponseI>,
      NullableAddUpdateDealerRequestT
    >({
      queryFn: async (data) => {
        try {
          const response = await service.addUpdateDealer(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['master_dealers'],
    }),
    downloadCarrierImagesAsZip: build.mutation<
      StoreBaseResponse<unknown>,
      number
    >({
      queryFn: async (carrierId) => {
        try {
          const response = await service.downloadCarrierImagesAsZip(carrierId);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getCarriersFromQueueId: build.query<
      StoreBaseResponse<CarriersFromSavedQueue[]>,
      string
    >({
      queryFn: async (carrierId) => {
        try {
          const response = await service.getCarriersFromQueueId(carrierId);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['settings_AddUpdateCarrierQueue'],
    }),

    uploadDealerExcel: build.mutation<
      StoreBaseResponse<UploadDealerStockResponseInterface[]>,
      FormData
    >({
      queryFn: async (formData) => {
        try {
          const response = await service.uploadExcelDealerStock(formData);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),

    updateDealerStock: build.mutation<
      StoreBaseResponse<null>,
      UploadDealerStockRequestInterface
    >({
      queryFn: async (payload) => {
        try {
          const data = await service.updateDealerStock(payload);
          return createCustomResponse(data);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCurrencyQuery,
  useGetCountriesQuery,
  useGetCarriersListQuery,
  useGetCarrierMakesQuery,
  useGetCarrierModelsQuery,
  useGetCustomersQuery,
  useGetCarrierStatusQuery,
  useGetCarrierTypesQuery,
  useGetImagesByCarrierIdQuery,
  useGetDocumentsByCarrierIdQuery,
  useGetCarrierHistoryQuery,
  useGetBookingInfoQuery,
  useGetCarrierByIdForEditQuery,
  useLazyGetCarrierByIdForEditQuery,
  useGetCarrierByIdForViewQuery,
  useGetAuctionHousesQuery,
  useGetLocationsQuery,
  useGetCarrierCategoriesQuery,
  useGetCarrierExportCategoriesQuery,
  useGetDealersQuery,
  useGetFuelTypesQuery,
  useGetSellersQuery,
  useGetStatusTypesQuery,
  useGetTransmissionTypesQuery,
  useGetAllCarriersQuery,
  useGetDeliverableCarriersQuery,
  useGetMasterSellersQuery,
  useGetMasterDealersQuery,
  useLazyGetCarriersFromQueueIdQuery,
  usePreviewCarrierDocumentMutation,

  useAddSellerMutation,
  useAddQuickVehicleMutation,
  useAddQuoteMutation,
  usePutBookingMutation,
  useGetCarriersListExportMutation,
  useUploadImagesMutation,
  useModifyImageSettingsMutation,
  useModifyImageSequenceMutation,
  useDownloadCarrierDocumentMutation,
  useUploadCarrierDocumentsMutation,
  useDeleteCarrierDocumentsMutation,
  useAddCarrierMutation,
  usePatchCostsMutation,
  usePatchMoreInfoMutation,
  usePatchVitalsMutation,
  usePatchCostPurchaseMutation,
  usePatchQuoteMutation,
  useMarkStatusAsReadyToDeliverMutation,
  useConfirmDeliveryOfDocumentsMutation,
  useGetAllMakeQuery,
  useAddMasterMakeMutation,
  useAddMasterMakeChangeMutation,
  useGetModelsByMakeMutation,
  useAddModelsByMakeMutation,
  useGetMasterAuctionHouseQuery,
  useShareByEmailMutation,
  useShareByEmailMultiCarriersMutation,
  useAddUpdateMastersSellersMutation,
  useAddUpdateMastersDealersMutation,
  useDownloadCarrierImagesAsZipMutation,
  useUploadDealerExcelMutation,
  useUpdateDealerStockMutation,
} = carriersApi;
